import React from "react";
import { Typography } from "antd";
import { CalendarOutlined, MessageOutlined, SmileOutlined, UserSwitchOutlined, FileTextOutlined, ThunderboltOutlined, PlusCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Title, Text } = Typography;

const ResponsiveTitle = styled(Title)`
  && {
    color: #6841eb;
    text-align: center;
    margin-bottom: 64px;
    font-weight: 800;
    font-size: 2.8rem;
    letter-spacing: -1px;
    @media (max-width: 768px) {
      font-size: 2.2rem;
      margin-bottom: 40px;
    }
    @media (max-width: 480px) {
      font-size: 1.9rem;
      margin-bottom: 30px;
      padding: 0 15px;
    }
  }
`;

const Section = styled.section`
  background: #f0f2f5;
  padding: 96px 0 64px 0;
  @media (max-width: 768px) {
    padding: 60px 0 40px 0;
  }
  @media (max-width: 480px) {
    padding: 40px 0 30px 0;
  }
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1300px;
  margin: 0 auto 112px auto;
  flex-direction: ${({ reverse }) => (reverse ? "row-reverse" : "row")};
  gap: 96px;
  @media (max-width: 1200px) {
    gap: 48px;
    max-width: 98vw;
  }
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 32px;
    margin-bottom: 56px;
    align-items: stretch;
  }
`;

const BrowserFrame = styled.div`
  width: 720px;
  max-width: 98vw;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 8px 48px rgba(104,65,235,0.13);
  transition: box-shadow 0.25s, transform 0.25s;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 460px;
  @media (max-width: 900px) {
    width: 100%;
    min-height: 220px;
    margin: 0 auto;
  }
  &:hover {
    box-shadow: 0 16px 64px rgba(104,65,235,0.18);
    transform: translateY(-4px) scale(1.02);
  }
`;

const BrowserTop = styled.div`
  height: 36px;
  background: #f5f6fa;
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  display: flex;
  align-items: center;
  padding: 0 18px;
  border-bottom: 1px solid #ececec;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  background: ${({ color }) => color};
`;

const FeatureImage = styled.img`
  width: 100%;
  height: 420px;
  object-fit: contain;
  display: block;
  border-bottom-left-radius: 22px;
  border-bottom-right-radius: 22px;
  background: #fff;
  @media (max-width: 900px) {
    height: 180px;
  }
`;

const FeatureTextBlock = styled.div`
  flex: 1;
  min-width: 320px;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0 12px;
  @media (max-width: 900px) {
    align-items: center;
    text-align: center;
    max-width: 100vw;
    padding: 0;
  }
`;

const IconCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #ede7fd;
  color: #6841eb;
  font-size: 36px;
  margin-bottom: 28px;
  box-shadow: 0 2px 12px rgba(104,65,235,0.08);
`;

const FeatureTitle = styled(Title).attrs({ level: 2 })`
  && {
    font-size: 2.4rem;
    font-weight: 800;
    color: #222;
    margin-bottom: 22px;
    text-align: left;
    letter-spacing: -1px;
    @media (max-width: 900px) {
      text-align: center;
      font-size: 2rem;
    }
    @media (max-width: 480px) {
      font-size: 1.7rem;
      margin-bottom: 16px;
    }
  }
`;

const FeatureDesc = styled(Text)`
  font-size: 1.35rem;
  color: #555;
  text-align: left;
  max-width: 520px;
  display: block;
  margin-bottom: 0;
  line-height: 1.6;
  @media (max-width: 900px) {
    text-align: center;
    max-width: 100vw;
    font-size: 1.2rem;
    padding: 0 15px;
  }
  @media (max-width: 480px) {
    font-size: 1.1rem;
    line-height: 1.5;
    padding: 0 10px;
  }
`;

const features = [
  {
    icon: <UserSwitchOutlined />,
    title: "Smart Talent Pool",
    description: "Build a high-quality talent pool with complete control. Only candidates you select are added—whether sourced outbound or applying inbound. Updates automatically as you engage, so no manual management required.",
    image: "/candidate pool.png",
    alt: "Smart Talent Pool"
  },
  {
    icon: <FileTextOutlined />,
    title: "Smart Profiles",
    description: "Candidates create comprehensive profiles with resume and all details. Recruiters can submit these complete profiles to hiring managers with one click—no manual formatting or data entry required.",
    image: "/smartprofile.gif",
    alt: "Smart Profiles"
  },
  {
    icon: <ThunderboltOutlined />,
    title: "Auto-staging",
    description: "Candidates automatically move through hiring stages based on decisions, not manual triggers. Live status updates keep candidates and hiring managers informed while your dashboard stays current—no manual stage management required.",
    image: "/AUTOSTAGING.png",
    alt: "Auto-staging"
  },
  {
    icon: <PlusCircleOutlined />,
    title: "Adding a Job",
    description: "Generate AI-powered job descriptions and invite candidates from any platform. The job automatically posts to your job board while outbound invites include full job details. One job creation serves both inbound and outbound sourcing seamlessly.",
    image: "/jobdescription (1).gif",
    alt: "Adding a Job"
  },
  {
    icon: <CheckCircleOutlined />,
    title: "Offer Placement",
    description: "Generate AI-powered offer letters by just providing the start date and timeline to accept the offer. Share directly on the platform with candidates, and upon acceptance, both recruiter and hiring manager receive the signed offer automatically—no manual document handling required.",
    image: "/offerletternew.png",
    alt: "Offer Placement"
  },
  {
    icon: <CalendarOutlined />,
    title: "Save 5 Hours/Week on Scheduling",
    description: "Native scheduling finds mutual availability and books interviews in one click. No more back-and-forth emails or external tools. Reduce candidate drop-off by 40%.",
    image: "/interview scheduling.gif",
    alt: "Smart Scheduling"
  },
  {
    icon: <MessageOutlined />,
    title: "Cut Communication Delays by 80%",
    description: "Built-in real-time chat for recruiters, hiring managers, and candidates. No more lost messages or switching between apps—just faster decisions.",
    image: "/gif for chatting.gif",
    alt: "Team Collaboration"
  },
  {
    icon: <SmileOutlined />,
    title: "Increase Candidate Retention by 60%",
    description: "AI-powered feedback gives every candidate actionable insights, keeping them engaged and more likely to apply again.",
    image: "/feedbackforrecruiters.gif",
    alt: "AI Feedback System"
  }
];

const Features = () => (
  <Section>
    <div style={{ maxWidth: 1300, margin: "0 auto", padding: "0 15px" }}>
      <ResponsiveTitle level={2}>
        See ChronoFlow in Action
      </ResponsiveTitle>
      {features.map((feature, idx) => (
        <FeatureRow key={feature.title} reverse={idx % 2 === 1}>
          <BrowserFrame>
            <BrowserTop>
              <Dot color="#fc625d" />
              <Dot color="#fdbc40" />
              <Dot color="#35cd4b" />
            </BrowserTop>
            <FeatureImage src={feature.image} alt={feature.alt} />
          </BrowserFrame>
          <FeatureTextBlock>
            <IconCircle>{feature.icon}</IconCircle>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDesc>{feature.description}</FeatureDesc>
          </FeatureTextBlock>
        </FeatureRow>
      ))}
    </div>
  </Section>
);

export default Features;